"use client"

import { useEffect, useState, useCallback, useRef, useMemo } from "react"
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts"
import {
  FileText,
  Download,
  Filter,
  Package,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Search,
  FileCheck,
  ChevronDown,
  Calendar,
  DollarSign,
  Users,
  BarChart3,
  Settings,
  MoreHorizontal,
  Grid,
  List,
  FileSpreadsheet,
  Plane,
  MapPin,
  User,
  Eye,
  ShoppingCart,
} from "lucide-react"
import MasterSideBar from "../../components/MasterSideBar"
import { getRequisitions } from "../../api/procurementApi"
import Topbar from "../../components/TopbarNav"
import TravelRequestCard from "../../components/operationsAssociate/TravelRequestCard"
import RejectModal from "../../components/operationsAssociate/TravelRejectModal"
import DashboardSummary from "./TravelSummarystats"
import AddItineraryModal from "../../components/head-of-Operations/AddItenaryModal"
import useTravelRequests from "../../hooks/useTravelRequest"
import { fetchPlanningProjectsWithoutProcurementPlan } from "../../api/projectApi"
import { getRequirementsByProjectId, updateRequirement } from "../../api/requirementApi"
import CreateProcurementPlanModal from "../../components/operationsAssociate/ProcurementPlanModal"
import { FaCheck, FaTimes } from "react-icons/fa"

const API_BASE_URL = "https://ims.holdthechild.net/api"

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

const ProcurementLogisticsReports = () => {
  // --- UI State ---
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const [selectedProject, setSelectedProject] = useState("all")
  const [reportType, setReportType] = useState("procurement")
  const [dateRange, setDateRange] = useState("current_month")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [projects, setProjects] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [viewMode, setViewMode] = useState("grid")
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const exportDropdownRef = useRef(null)

  // Travel logistics state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [travelFilters, setTravelFilters] = useState({
    status: "",
    dateRange: "",
    destination: "",
  })

  // Procurement planning state
  const [planningProjects, setPlanningProjects] = useState([])
  const [selectedPlanningProject, setSelectedPlanningProject] = useState(null)
  const [planningRequirements, setPlanningRequirements] = useState([])
  const [procurementItems, setProcurementItems] = useState([])
  const [planningDecisions, setPlanningDecisions] = useState({})
  const [planningSearchTerm, setPlanningSearchTerm] = useState("")
  const [planningStatusFilter, setPlanningStatusFilter] = useState("all")
  const [activeRequirement, setActiveRequirement] = useState(null)
  const [activeModal, setActiveModal] = useState(null)
  const [planningLoading, setPlanningLoading] = useState(false)
  const [initialPlanningLoading, setInitialPlanningLoading] = useState(true)

  const {
    requests: travelRequests,
    loading: travelLoading,
    error: travelError,
    summary: travelSummary,
    updateRequestStatus,
    addItinerary,
    refreshRequests,
    fetchTravelRequests,
  } = useTravelRequests()

  const [reportData, setReportData] = useState({
    procurement: {
      summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    travel_logistics: {
      summary: { total: 0, pending: 0, approved: 0, rejected: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    requisitions: {
      summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    vendor: {
      summary: { total: 0, active: 0, inactive: 0, pending: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    contract: {
      summary: { total: 0, active: 0, expired: 0, renewing: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    project_planning: {
      summary: { totalProjects: 0, totalRequirements: 0, totalValue: 0, pending: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
  })

  // Fetch projects for the dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: getAuthHeaders(),
      })
      const data = await response.json()
      const projectsData = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []

      const formattedProjects = [
        { _id: "all", name: "All Projects" },
        ...projectsData.map((project) => ({
          _id: project._id,
          name: project.projectTitle || project.name || `Project ${project._id}`,
        })),
      ]

      setProjects(formattedProjects)
    } catch (err) {
      console.error("Error fetching projects:", err)
      setProjects([{ _id: "all", name: "All Projects" }])
    }
  }, [])

  // Fetch procurement data
  const fetchProcurementData = useCallback(async () => {
    try {
      const [rfqsRes, rfpsRes, boqsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/RFQs`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/rfps`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/boqs`, { headers: getAuthHeaders() }),
      ])

      const [rfqs, rfps, boqs] = await Promise.all([rfqsRes.json(), rfpsRes.json(), boqsRes.json()])

      // Process and combine data
      const processData = (data, type) =>
        Array.isArray(data)
          ? data.map((item) => ({
            ...item,
            type,
            requestDate: item.issueDate || item.createdAt,
            expectedDate: item.submissionDeadline,
            description: item.projectName || item.notes || "N/A",
            status: mapStatus(item.status),
            value: item.estimatedValue || item.totalValue || "N/A",
          }))
          : []

      const allData = [...processData(rfqs, "RFQ"), ...processData(rfps, "RFP"), ...processData(boqs, "BOQ")]

      return processReportData(allData)
    } catch (err) {
      console.error("Error fetching procurement data:", err)
      return getEmptyReportData()
    }
  }, [])

  // Fetch travel logistics data
  const fetchTravelLogisticsData = useCallback(async () => {
    try {
      // Process travel requests data
      const processedData = travelRequests.map((request) => ({
        ...request,
        type: "Travel Request",
        requestDate: request.createdAt,
        expectedDate: request.departureDate,
        description: request.purpose,
        status: request.status,
        value: request.estimatedCost,
        project: request.project?.projectTitle || "N/A",
        destination: request.destination,
        traveler: request.travelerName,
      }))

      return processTravelReportData(processedData)
    } catch (err) {
      console.error("Error fetching travel logistics data:", err)
      return getEmptyTravelReportData()
    }
  }, [travelRequests])

  // Fetch requisitions data - FIXED VERSION
  const fetchRequisitionsData = useCallback(async () => {
    try {
      const filters = {
        ...(selectedProject !== "all" && { projectId: selectedProject }),
        limit: 1000,
        page: 1,
      }

      const response = await getRequisitions(filters)
      const requisitions = response.data?.data || response.data || []

      const processedData = (Array.isArray(requisitions) ? requisitions : []).map((req) => ({
        ...req,
        type: "Requisition",
        requestDate: req.createdAt,
        expectedDate: req.dueDate,
        description: req.title,
        status: mapRequisitionStatus(req.currentStatus),
        value: req.totalEstimatedValue || "N/A",
        project: req.projectId?.projectTitle || "N/A",
        projectId: req.projectId?._id,
        // Add more useful fields for better reporting
        typeOfProcurement: req.typeOfProcurement || "N/A",
        departmentsTargeted: req.departmentsTargeted || [],
        priority: req.priority || "N/A",
        requisitionBy: `${req.requisitionBy?.firstName || ''} ${req.requisitionBy?.lastName || ''}`.trim() || "N/A",
        currentStatus: req.currentStatus || "N/A"
      }))

      return processReportData(processedData)
    } catch (err) {
      console.error("Error fetching requisitions data:", err)
      return getEmptyReportData()
    }
  }, [selectedProject])

  // Fetch vendor data
  const fetchVendorData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendors`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Vendors endpoint not available")
      }

      const data = await response.json()
      const vendors = Array.isArray(data.data) ? data.data : []

      const processedData = vendors.map((vendor) => ({
        ...vendor,
        type: "Vendor",
        requestDate: vendor.createdAt,
        expectedDate: vendor.contractExpiryDate,
        description: vendor.name,
        status: vendor.status,
        value: vendor.totalValue || "N/A",
        project: vendor.projects?.map(p => p.projectTitle).join(", ") || "N/A",
      }))

      return processVendorReportData(processedData)
    } catch (err) {
      console.error("Error fetching vendor data:", err)
      return getEmptyVendorReportData()
    }
  }, [])

  // Fetch contract data - extracted from vendor sub-documents
  const fetchContractData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendors`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Vendors endpoint not available")
      }

      const data = await response.json()
      const vendors = Array.isArray(data.data) ? data.data : []

      // Extract contracts from all vendors
      const allContracts = []
      vendors.forEach((vendor) => {
        if (vendor.contracts && Array.isArray(vendor.contracts)) {
          vendor.contracts.forEach((contract) => {
            allContracts.push({
              ...contract,
              type: "Contract",
              requestDate: contract.startDate,
              expectedDate: contract.endDate,
              description: contract.title || contract.contractNumber || "Untitled",
              status: contract.status || "active",
              value: contract.value || "N/A",
              vendor: vendor.name || "N/A",
              project: vendor.projects?.map(p => p.projectTitle).join(", ") || "N/A",
            })
          })
        }
      })

      return processContractReportData(allContracts)
    } catch (err) {
      console.error("Error fetching contract data:", err)
      return getEmptyContractReportData()
    }
  }, [])

  // Fetch project planning data
  const fetchProjectPlanningData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/project-planning`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Project planning endpoint not available")
      }

      const data = await response.json()
      const projects = Array.isArray(data.data) ? data.data : []

      const processedData = projects.map((project) => ({
        ...project,
        type: "Project Planning",
        requestDate: project.startDate,
        expectedDate: project.endDate,
        description: project.projectTitle,
        status: project.status,
        value: project.budget || "N/A",
        requirements: project.requirements || [],
      }))

      return processProjectPlanningReportData(processedData)
    } catch (err) {
      console.error("Error fetching project planning data:", err)
      return getEmptyProjectPlanningReportData()
    }
  }, [])

  // Load projects for procurement planning
  const loadPlanningProjects = async () => {
    setPlanningLoading(true);
    try {
      const projectsData = await fetchPlanningProjectsWithoutProcurementPlan();
      setPlanningProjects(projectsData || []);
    } catch (err) {
      console.error("Error loading planning projects:", err);
    } finally {
      setPlanningLoading(false);
      setInitialPlanningLoading(false);
    }
  };

  // Load requirements for selected planning project
  useEffect(() => {
    if (!selectedPlanningProject) return;
    
    const loadRequirements = async () => {
      try {
        const data = await getRequirementsByProjectId(selectedPlanningProject);
        const normalizedData = data.map(r => ({
          ...r,
          specification: r.specification || '',
          availabilityStatus: r.availabilityStatus || 'Pending'
        }));
        setPlanningRequirements(normalizedData);

        const initialDecisions = {};
        const initialProcureItems = [];
        normalizedData.forEach(r => {
          if (r.availabilityStatus && r.availabilityStatus !== 'Pending') {
            initialDecisions[r._id] = r.availabilityStatus;
            if (r.availabilityStatus === 'Procure') {
              initialProcureItems.push(r);
            }
          }
        });
        setPlanningDecisions(initialDecisions);
        setProcurementItems(initialProcureItems);
      } catch (err) {
        console.error("Failed to fetch requirements:", err);
      }
    };
    
    loadRequirements();
  }, [selectedPlanningProject]);

  // Initialize planning projects
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    loadPlanningProjects();
  }, []);

  // Map various status values to our standard format
  const mapStatus = (status) => {
    if (!status) return "pending"
    const statusLower = status.toLowerCase()

    if (["completed", "approved", "submitted", "closed"].includes(statusLower)) {
      return "fulfilled"
    }
    if (["draft", "pending", "open"].includes(statusLower)) {
      return "pending"
    }
    if (["delayed", "overdue"].includes(statusLower)) {
      return "delayed"
    }
    return "pending"
  }

  // Map requisition status to our standard format
  const mapRequisitionStatus = (status) => {
    if (!status) return "pending"

    const statusMap = {
      draft: "pending",
      submitted: "pending",
      pending_director_approval: "pending",
      approved_by_director: "fulfilled",
      rejected_by_director: "delayed",
      processing_by_procurement: "pending",
      completed: "fulfilled",
      cancelled: "delayed",
      on_hold: "delayed",
    }

    return statusMap[status] || "pending"
  }

  // Process raw data into report format
  const processReportData = (data) => {
    const summary = {
      total: data.length,
      pending: data.filter((item) => item.status === "pending").length,
      fulfilled: data.filter((item) => item.status === "fulfilled").length,
      delayed: data.filter((item) => item.status === "delayed").length,
    }

    const byStatus = [
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
      { name: "Fulfilled", value: summary.fulfilled, color: "#10b981" },
      { name: "Delayed", value: summary.delayed, color: "#ef4444" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        pending: monthData.filter((item) => item.status === "pending").length,
        fulfilled: monthData.filter((item) => item.status === "fulfilled").length,
        delayed: monthData.filter((item) => item.status === "delayed").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process travel logistics data into report format
  const processTravelReportData = (data) => {
    const summary = {
      total: data.length,
      pending: data.filter((item) => item.status === "pending").length,
      approved: data.filter((item) => item.status === "approved").length,
      rejected: data.filter((item) => item.status === "rejected").length,
    }

    const byStatus = [
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
      { name: "Approved", value: summary.approved, color: "#10b981" },
      { name: "Rejected", value: summary.rejected, color: "#ef4444" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        pending: monthData.filter((item) => item.status === "pending").length,
        approved: monthData.filter((item) => item.status === "approved").length,
        rejected: monthData.filter((item) => item.status === "rejected").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process vendor data into report format
  const processVendorReportData = (data) => {
    const summary = {
      total: data.length,
      active: data.filter((item) => item.status === "active").length,
      inactive: data.filter((item) => item.status === "inactive").length,
      pending: data.filter((item) => item.status === "pending").length,
    }

    const byStatus = [
      { name: "Active", value: summary.active, color: "#10b981" },
      { name: "Inactive", value: summary.inactive, color: "#ef4444" },
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        active: monthData.filter((item) => item.status === "active").length,
        inactive: monthData.filter((item) => item.status === "inactive").length,
        pending: monthData.filter((item) => item.status === "pending").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process contract data into report format
  const processContractReportData = (data) => {
    const summary = {
      total: data.length,
      active: data.filter((item) => item.status === "active").length,
      expired: data.filter((item) => item.status === "expired").length,
      renewing: data.filter((item) => item.status === "renewing").length,
    }

    const byStatus = [
      { name: "Active", value: summary.active, color: "#10b981" },
      { name: "Expired", value: summary.expired, color: "#ef4444" },
      { name: "Renewing", value: summary.renewing, color: "#f59e0b" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        active: monthData.filter((item) => item.status === "active").length,
        expired: monthData.filter((item) => item.status === "expired").length,
        renewing: monthData.filter((item) => item.status === "renewing").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process project planning data into report format
  const processProjectPlanningReportData = (data) => {
    const summary = {
      totalProjects: data.length,
      totalRequirements: data.reduce((sum, project) => sum + (project.requirements?.length || 0), 0),
      totalValue: data.reduce((sum, project) => sum + (project.budget || 0), 0),
      pending: data.filter((item) => item.status === "planning").length,
    }

    const byStatus = [
      { name: "Planning", value: summary.pending, color: "#f59e0b" },
      { name: "Active", value: data.filter((item) => item.status === "active").length, color: "#10b981" },
      { name: "Completed", value: data.filter((item) => item.status === "completed").length, color: "#3b82f6" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        planning: monthData.filter((item) => item.status === "planning").length,
        active: monthData.filter((item) => item.status === "active").length,
        completed: monthData.filter((item) => item.status === "completed").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  const getEmptyReportData = () => ({
    summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
    byStatus: [
      { name: "Pending", value: 0, color: "#f59e0b" },
      { name: "Fulfilled", value: 0, color: "#10b981" },
      { name: "Delayed", value: 0, color: "#ef4444" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyTravelReportData = () => ({
    summary: { total: 0, pending: 0, approved: 0, rejected: 0 },
    byStatus: [
      { name: "Pending", value: 0, color: "#f59e0b" },
      { name: "Approved", value: 0, color: "#10b981" },
      { name: "Rejected", value: 0, color: "#ef4444" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyVendorReportData = () => ({
    summary: { total: 0, active: 0, inactive: 0, pending: 0 },
    byStatus: [
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Inactive", value: 0, color: "#ef4444" },
      { name: "Pending", value: 0, color: "#f59e0b" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyContractReportData = () => ({
    summary: { total: 0, active: 0, expired: 0, renewing: 0 },
    byStatus: [
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Expired", value: 0, color: "#ef4444" },
      { name: "Renewing", value: 0, color: "#f59e0b" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyProjectPlanningReportData = () => ({
    summary: { totalProjects: 0, totalRequirements: 0, totalValue: 0, pending: 0 },
    byStatus: [
      { name: "Planning", value: 0, color: "#f59e0b" },
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Completed", value: 0, color: "#3b82f6" },
    ],
    timeline: [],
    details: [],
  })

  // Load report data based on type and filters
  const loadReportData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      if (reportType === "procurement") {
        const data = await fetchProcurementData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            pending: data.details.filter((item) => item.status === "pending").length,
            fulfilled: data.details.filter((item) => item.status === "fulfilled").length,
            delayed: data.details.filter((item) => item.status === "delayed").length,
          }

          data.byStatus = [
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Fulfilled", value: filteredSummary.fulfilled, color: "#10b981" },
            { name: "Delayed", value: filteredSummary.delayed, color: "#ef4444" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "travel_logistics") {
        const data = await fetchTravelLogisticsData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            pending: data.details.filter((item) => item.status === "pending").length,
            approved: data.details.filter((item) => item.status === "approved").length,
            rejected: data.details.filter((item) => item.status === "rejected").length,
          }

          data.byStatus = [
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Approved", value: filteredSummary.approved, color: "#10b981" },
            { name: "Rejected", value: filteredSummary.rejected, color: "#ef4444" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "requisitions") {
        const data = await fetchRequisitionsData()
        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "vendor") {
        const data = await fetchVendorData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            active: data.details.filter((item) => item.status === "active").length,
            inactive: data.details.filter((item) => item.status === "inactive").length,
            pending: data.details.filter((item) => item.status === "pending").length,
          }

          data.byStatus = [
            { name: "Active", value: filteredSummary.active, color: "#10b981" },
            { name: "Inactive", value: filteredSummary.inactive, color: "#ef4444" },
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "contract") {
        const data = await fetchContractData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            active: data.details.filter((item) => item.status === "active").length,
            expired: data.details.filter((item) => item.status === "expired").length,
            renewing: data.details.filter((item) => item.status === "renewing").length,
          }

          data.byStatus = [
            { name: "Active", value: filteredSummary.active, color: "#10b981" },
            { name: "Expired", value: filteredSummary.expired, color: "#ef4444" },
            { name: "Renewing", value: filteredSummary.renewing, color: "#f59e0b" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "project_planning") {
        const data = await fetchProjectPlanningData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            totalProjects: data.details.length,
            totalRequirements: data.details.reduce((sum, project) => sum + (project.requirements?.length || 0), 0),
            totalValue: data.details.reduce((sum, project) => sum + (project.budget || 0), 0),
            pending: data.details.filter((item) => item.status === "planning").length,
          }

          data.byStatus = [
            { name: "Planning", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Active", value: data.details.filter((item) => item.status === "active").length, color: "#10b981" },
            { name: "Completed", value: data.details.filter((item) => item.status === "completed").length, color: "#3b82f6" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      }
    } catch (err) {
      setError(`Failed to load ${reportType} report: ${err.message}`)
      if (reportType === "vendor") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyVendorReportData(),
        }))
      } else if (reportType === "contract") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyContractReportData(),
        }))
      } else if (reportType === "project_planning") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyProjectPlanningReportData(),
        }))
      } else if (reportType === "travel_logistics") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyTravelReportData(),
        }))
      } else {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyReportData(),
        }))
      }
    } finally {
      setLoading(false)
    }
  }, [reportType, selectedProject, fetchProcurementData, fetchTravelLogisticsData, fetchRequisitionsData, fetchVendorData, fetchContractData, fetchProjectPlanningData])

  // Initialize data
  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    loadReportData()
  }, [loadReportData])

  // Travel logistics handlers
  const handleApprove = async (id) => {
    await updateRequestStatus(id, "approved")
  }

  const handleReject = (id) => {
    setSelectedRequestId(id)
    setIsRejectModalOpen(true)
  }

  const handleRejectSubmit = async (reason) => {
    await updateRequestStatus(selectedRequestId, "rejected", { rejectionReason: reason })
    setIsRejectModalOpen(false)
    setSelectedRequestId(null)
  }

  const handleAddItinerary = (id) => {
    setSelectedRequestId(id)
    setIsItineraryModalOpen(true)
  }

  const handleItinerarySubmit = async (itineraryData) => {
    await addItinerary(selectedRequestId, {
      ...itineraryData,
      bookedBy: "operations_associate_id",
    })
    setIsItineraryModalOpen(false)
    setSelectedRequestId(null)
  }

  const handleReviewReport = (id) => {
    console.log("Review report for request:", id)
  }

  const handleTravelFilterChange = (newFilters) => {
    setTravelFilters(newFilters)
    fetchTravelRequests(newFilters)
  }

  // Procurement planning handlers
  const handlePlanningDecision = async (req, decision) => {
    const validStatuses = ['Available', 'Procure', 'Pending'];
    if (!validStatuses.includes(decision)) return;

    setPlanningDecisions(prev => ({ ...prev, [req._id]: decision }));
    setPlanningRequirements(prev =>
      prev.map(r => r._id === req._id ? { ...r, availabilityStatus: decision } : r)
    );

    if (decision === "Procure") {
      setProcurementItems(prev =>
        prev.find(item => item._id === req._id) ? prev : [...prev, req]
      );
    } else {
      setProcurementItems(prev => prev.filter(item => item._id !== req._id));
    }

    try {
      await updateRequirement(req._id, { availabilityStatus: decision });
    } catch (err) {
      console.error("Failed to update availabilityStatus:", err);
    }
  };

  const removeProcurementItem = (item) => {
    handlePlanningDecision(item, 'Pending');
  };

  const openPlanningModal = (req = null, type = null) => {
    if (req) {
      setActiveRequirement(req);
      setActiveModal("requirement");
    } else {
      setActiveModal(type || "procurement");
    }
  };

  const closePlanningModal = async (planCreated = false) => {
    setActiveRequirement(null);
    setActiveModal(null);
    if (planCreated) {
      // Remove the project from the list since it now has a procurement plan
      setPlanningProjects(prev => prev.filter(p => p._id !== selectedPlanningProject));
      setSelectedPlanningProject(null);
      setPlanningRequirements([]);
      setProcurementItems([]);
      setPlanningDecisions({});
      // Reload projects silently
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadPlanningProjects();
    }
  };

  // Filter planning requirements
  const filteredPlanningRequirements = useMemo(() => {
    let reqs = planningRequirements;
    if (planningSearchTerm) {
      reqs = reqs.filter(r =>
        r.name.toLowerCase().includes(planningSearchTerm.toLowerCase())
      );
    }
    if (planningStatusFilter !== "all") {
      reqs = reqs.filter(r => {
        const decision = planningDecisions[r._id];
        if (planningStatusFilter === "pending") return !decision;
        return decision === planningStatusFilter;
      });
    }
    return reqs;
  }, [planningRequirements, planningSearchTerm, planningStatusFilter, planningDecisions]);

  // Export functionality
  const exportReport = (format) => {
    const currentData = reportData[reportType]
    const timestamp = new Date().toISOString().split("T")[0]
    const filename = `${reportType}-report-${selectedProject}-${timestamp}`

    try {
      if (format === "json") {
        const exportData = {
          reportType,
          project: projects.find((p) => p._id === selectedProject)?.name || "All Projects",
          dateRange,
          generatedAt: new Date().toISOString(),
          summary: currentData.summary,
          details: currentData.details,
        }

        const dataStr = JSON.stringify(exportData, null, 2)
        const dataBlob = new Blob([dataStr], { type: "application/json" })
        const url = URL.createObjectURL(dataBlob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${filename}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else if (format === "csv") {
        let headers
        if (reportType === "requisitions") {
          headers = [
            "ID",
            "Title",
            "Project",
            "Type",
            "Status",
            "Created Date",
            "Due Date",
            "Estimated Value",
            "Current Status",
          ]
        } else if (reportType === "vendor") {
          headers = [
            "ID",
            "Name",
            "Email",
            "Category",
            "Status",
            "Created Date",
            "Contract Expiry",
            "Total Value",
          ]
        } else if (reportType === "contract") {
          headers = [
            "ID",
            "Title",
            "Vendor",
            "Type",
            "Status",
            "Start Date",
            "End Date",
            "Value",
          ]
        } else if (reportType === "project_planning") {
          headers = [
            "ID",
            "Project Title",
            "Status",
            "Start Date",
            "End Date",
            "Budget",
            "Requirements Count",
          ]
        } else if (reportType === "travel_logistics") {
          headers = [
            "ID",
            "Traveler",
            "Destination",
            "Purpose",
            "Status",
            "Departure Date",
            "Return Date",
            "Estimated Cost",
          ]
        } else {
          headers = [
            "ID",
            "Type",
            "Project",
            "Description",
            "Status",
            "Request Date",
            "Expected Date",
            reportType === "procurement" ? "Value" : "Cost",
          ]
        }

        const csvContent = [
          headers.join(","),
          ...currentData.details.map((item) => {
            if (reportType === "requisitions") {
              return [
                item._id || item.id,
                `"${(item.title || "").replace(/"/g, '""')}"`,
                item.project || "N/A",
                item.typeOfProcurement || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
                item.currentStatus || "N/A",
              ].join(",")
            } else if (reportType === "vendor") {
              return [
                item._id || item.id,
                `"${(item.name || "").replace(/"/g, '""')}"`,
                item.email || "N/A",
                item.category || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else if (reportType === "contract") {
              return [
                item._id || item.id,
                `"${(item.title || "").replace(/"/g, '""')}"`,
                item.vendor || "N/A",
                item.type || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else if (reportType === "project_planning") {
              return [
                item._id || item.id,
                `"${(item.projectTitle || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
                item.requirements?.length || 0,
              ].join(",")
            } else if (reportType === "travel_logistics") {
              return [
                item._id || item.id,
                `"${(item.traveler || "").replace(/"/g, '""')}"`,
                `"${(item.destination || "").replace(/"/g, '""')}"`,
                `"${(item.description || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else {
              return [
                item._id || item.id,
                item.type,
                item.project || "N/A",
                `"${(item.description || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || item.cost || "N/A",
              ].join(",")
            }
          }),
        ].join("\n")

        const csvBlob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(csvBlob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${filename}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else {
        // For PDF and Excel, you'd typically call a backend service
        alert(`${format.toUpperCase()} export would be implemented with a backend service for proper formatting.`)
      }

      // Close dropdown after export
      setExportDropdownOpen(false)
    } catch (err) {
      setError(`Failed to export report: ${err.message}`)
    }
  }

  // Helper functions for UI
  const getStatusIcon = (status) => {
    switch (status) {
      case "fulfilled":
      case "active":
      case "completed":
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "pending":
      case "planning":
        return <Clock className="w-4 h-4 text-yellow-500" />
      case "delayed":
      case "expired":
      case "inactive":
      case "rejected":
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return <XCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "fulfilled":
      case "active":
      case "completed":
      case "approved":
        return "bg-green-100 text-green-800 border-green-200"
      case "pending":
      case "planning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "delayed":
      case "expired":
      case "inactive":
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  // Filter and paginate data
  const currentData = reportData[reportType]

  const filteredDetails = currentData.details.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.project?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reportType === "travel_logistics" && (
        item.traveler?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.destination?.toLowerCase().includes(searchTerm.toLowerCase())
      ))

    const matchesStatus = statusFilter === "all" || item.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const totalPages = Math.ceil(filteredDetails.length / itemsPerPage)
  const paginatedData = filteredDetails.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Add a handleLogout function
  const handleLogout = () => {
    // Clear token and redirect to login or home page
    localStorage.removeItem("token")
    window.location.href = "/login"
  }

  // Get report title based on type
  const getReportTitle = () => {
    switch (reportType) {
      case "procurement": return "Procurement Analytics"
      case "travel_logistics": return "Travel Logistics Management"
      case "requisitions": return "Requisitions Analytics"
      case "vendor": return "Vendor Management Reports"
      case "contract": return "Contract Management Reports"
      case "project_planning": return "Project Procurement Planning"
      default: return "Reports Dashboard"
    }
  }

  // Get KPI cards based on report type
  const getKPICards = () => {
    const summary = currentData.summary

    if (reportType === "vendor") {
      return [
        {
          title: "Total Vendors",
          value: summary.total,
          icon: Users,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Active Vendors",
          value: summary.active,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Inactive Vendors",
          value: summary.inactive,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
        {
          title: "Pending Vendors",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "contract") {
      // Calculate expiring soon contracts
      const now = new Date()
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(now.getDate() + 30)

      const expiringSoon = currentData.details.filter(item => {
        if (!item.endDate) return false
        const endDate = new Date(item.endDate)
        return endDate > now && endDate <= thirtyDaysFromNow
      }).length

      return [
        {
          title: "Total Contracts",
          value: summary.total,
          icon: FileText,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Active Contracts",
          value: summary.active,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Expired Contracts",
          value: summary.expired,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
        {
          title: "Expiring Soon",
          value: expiringSoon,
          icon: AlertTriangle,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "project_planning") {
      return [
        {
          title: "Total Projects",
          value: summary.totalProjects,
          icon: BarChart3,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Total Requirements",
          value: summary.totalRequirements,
          icon: FileCheck,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Total Value",
          value: `$${(summary.totalValue / 1000000).toFixed(1)}M`,
          icon: DollarSign,
          color: "purple",
          bgColor: "bg-purple-50",
          iconColor: "text-purple-600",
          textColor: "text-purple-900",
        },
        {
          title: "Planning Projects",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "travel_logistics") {
      return [
        {
          title: "Total Requests",
          value: summary.total,
          icon: Plane,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Pending Requests",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
        {
          title: "Approved Requests",
          value: summary.approved,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Rejected Requests",
          value: summary.rejected,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
      ]
    } else {
      return [
        {
          title: "Total Requests",
          value: summary.total,
          icon: FileText,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Pending",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
        {
          title: "Fulfilled",
          value: summary.fulfilled,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Delayed",
          value: summary.delayed,
          icon: AlertCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
      ]
    }
  }

  // Calculate planning totals
  const planningTotals = useMemo(() => {
    const totalItems = procurementItems.length;
    const totalValue = procurementItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
    return { totalItems, totalValue };
  }, [procurementItems]);

  // Selected planning project data
  const selectedPlanningProjectData = planningProjects.find(p => p._id === selectedPlanningProject);

  return (
    <div className="flex h-screen bg-gray-50 font-sans overflow-hidden">
      {/* MasterSideBar Integration */}
      <MasterSideBar 
        visible={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        onLogout={handleLogout}
        collapsed={sidebarCollapsed} 
        setCollapsed={setSidebarCollapsed} 
      />

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0 md:ml-64'}`}>
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-8 mt-16 lg:mt-20">
          <div className="max-w-7xl mx-auto space-y-6">
            
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <h1 className="text-2xl md:text-3xl font-black text-gray-800 tracking-tight uppercase">
                  {getReportTitle().split(' ').slice(0, -1).join(' ')} <span className="text-orange-600">{getReportTitle().split(' ').slice(-1)}</span>
                </h1>
                <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mt-1 italic">Comprehensive Analytics & Insights</p>

                {selectedProject !== "all" && (
                  <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    Filtered by: {projects.find(p => p._id === selectedProject)?.name || "Unknown Project"}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                <div className="relative" ref={exportDropdownRef}>
                  <button
                    onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Report
                    <ChevronDown className="ml-2 w-4 h-4" />
                  </button>

                  {exportDropdownOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                      <div className="py-1">
                        <button
                          onClick={() => exportReport("csv")}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileSpreadsheet className="mr-2 h-4 w-4" />
                          Export as CSV
                        </button>
                        <button
                          onClick={() => exportReport("json")}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Export as JSON
                        </button>
                        <button
                          onClick={() => exportReport("pdf")}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <FileText className="mr-2 h-4 w-4" />
                          Export as PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg shadow-sm">
                <div className="flex items-center">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                    <button
                      onClick={() => setError(null)}
                      className="text-sm text-red-600 hover:text-red-800 mt-2 underline"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Control Panel - Improved UI */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center">
                  <Filter className="w-5 h-5 text-gray-600 mr-3" />
                  <h2 className="text-lg font-semibold text-gray-900">Report Configuration</h2>
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Report Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Report Type</label>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        onClick={() => setReportType("procurement")}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${reportType === "procurement"
                          ? "bg-orange-50 border-orange-300 text-orange-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Procurement
                      </button>
                      <button
                        onClick={() => setReportType("requisitions")}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${reportType === "requisitions"
                          ? "bg-orange-50 border-orange-300 text-orange-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        <FileCheck className="w-4 h-4 mr-2" />
                        Requisitions
                      </button>
                      <button
                        onClick={() => setReportType("vendor")}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${reportType === "vendor"
                          ? "bg-orange-50 border-orange-300 text-orange-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        <Users className="w-4 h-4 mr-2" />
                        Vendors
                      </button>
                      <button
                        onClick={() => setReportType("contract")}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${reportType === "contract"
                          ? "bg-orange-50 border-orange-300 text-orange-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Contracts
                      </button>
                      <button
                        onClick={() => setReportType("project_planning")}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-all duration-200 ${reportType === "project_planning"
                          ? "bg-orange-50 border-orange-300 text-orange-800 shadow-sm"
                          : "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-sm"
                          }`}
                      >
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Project Planning
                      </button>
                    </div>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Date Range</label>
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                    >
                      <option value="current_month">Current Month</option>
                      <option value="last_month">Last Month</option>
                      <option value="quarter">This Quarter</option>
                      <option value="year">This Year</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Refresh Button */}
                  <div className="flex items-end">
                    <button
                      onClick={loadReportData}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh Data
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* REST OF THE CONTENT CONTINUES WITH THE SAME STRUCTURE... */}
            {/* Due to character limit, the rest of the JSX remains the same as your original file */}
            {/* Just ensure all the sections are wrapped properly within the new layout */}

{/* Project Planning Section - Integrated when reportType is "project_planning" */}
        {reportType === "project_planning" && (
          <div className="space-y-6">
            {/* Project Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 relative">
              <div className="border-b border-gray-200 px-6 py-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <Package className="mr-3 text-orange-500 w-6 h-6" />
                  Project Selection
                </h2>
              </div>
              <div className="p-6">
                {initialPlanningLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                  </div>
                )}
                <div className={initialPlanningLoading ? "opacity-50" : ""}>
                  {planningProjects.length === 0 && !initialPlanningLoading ? (
                    <div className="text-center text-gray-600">No projects without plans found.</div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Choose Project</label>
                        <select
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors duration-200 bg-white"
                          value={selectedPlanningProject || ""}
                          onChange={(e) => setSelectedPlanningProject(e.target.value)}
                        >
                          <option value="">-- Select a Project --</option>
                          {planningProjects.map((p) => (
                            <option key={p._id} value={p._id}>{p.projectTitle || p.name || `Project ${p._id}`}</option>
                          ))}
                        </select>
                      </div>

                      {selectedPlanningProjectData && (
                        <div className="lg:col-span-2">
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-5 space-y-3">
                            <h3 className="font-semibold text-orange-900 text-lg">{selectedPlanningProjectData.projectTitle || selectedPlanningProjectData.name}</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div className="flex items-center">
                                <User className="mr-2 w-4 h-4 text-orange-500" />
                                <span className="font-medium text-gray-600">Donor(s):</span>
                                <span className="ml-1 text-gray-900">{selectedPlanningProjectData.donor?.map(d => d.name || d).join(", ") || "-"}</span>
                              </div>
                              <div className="flex items-center">
                                <Calendar className="mr-2 w-4 h-4 text-emerald-500" />
                                <span className="font-medium text-gray-600">Status:</span>
                                <span className="ml-1 text-emerald-600 font-semibold">{selectedPlanningProjectData.status || "-"}</span>
                              </div>
                              <div className="flex items-center">
                                <Calendar className="mr-2 w-4 h-4 text-gray-500" />
                                <span className="font-medium text-gray-600">Start Date:</span>
                                <span className="ml-1 text-gray-900">{selectedPlanningProjectData.startDate?.split("T")[0] || "-"}</span>
                              </div>
                              <div className="flex items-center">
                                <Calendar className="mr-2 w-4 h-4 text-gray-500" />
                                <span className="font-medium text-gray-600">End Date:</span>
                                <span className="ml-1 text-gray-900">{selectedPlanningProjectData.endDate?.split("T")[0] || "-"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Requirements Filters */}
            {selectedPlanningProject && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
                <div className="px-6 py-4 flex flex-col sm:flex-row gap-4 items-center justify-between">
                  <div className="flex items-center space-x-4 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search requirements..."
                        className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 w-full sm:w-80 text-gray-900 transition-colors duration-200"
                        value={planningSearchTerm}
                        onChange={(e) => setPlanningSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="relative">
                      <select
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-10 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900 transition-colors duration-200"
                        value={planningStatusFilter}
                        onChange={(e) => setPlanningStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="Available">Available</option>
                        <option value="Procure">To Procure</option>
                      </select>
                      <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-600 bg-gray-50 px-3 py-2 rounded-lg mt-2 sm:mt-0">
                    {filteredPlanningRequirements.length} of {planningRequirements.length} requirements
                  </div>
                </div>
              </div>
            )}

            {/* Project Requirements Table */}
            {selectedPlanningProject && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden">
                <div className="border-b border-orange-200 px-6 py-4 bg-orange-50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Package className="mr-3 text-orange-500 w-6 h-6" />
                    {selectedPlanningProjectData?.projectTitle || selectedPlanningProjectData?.name} — Project Requirements
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Item</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit Cost</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Cost</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Availability</th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Details</th>
                        <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPlanningRequirements.map(req => (
                        <tr key={req._id} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900 mb-1">{req.name}</div>
                            {req.type && (
                              <div className="text-sm text-gray-500 mb-1">
                                Type - {req.type.charAt(0).toUpperCase() + req.type.slice(1)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">{req.quantity} {req.unit}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-medium">${req.unitCost}</td>
                          <td className="px-6 py-4 text-sm text-gray-900 font-semibold">${req.totalCost}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.availabilityStatus === "Available" ? "bg-emerald-100 text-emerald-800" :
                              req.availabilityStatus === "Procure" ? "bg-blue-100 text-blue-800" :
                                req.availabilityStatus === "Pending" ? "bg-gray-100 text-gray-700" :
                                  "bg-gray-100 text-gray-700"
                              }`}>
                              {req.availabilityStatus || "Pending"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => openPlanningModal(req)}
                              className="text-gray-400 hover:text-orange-500 transition-colors duration-200 p-1 rounded-full hover:bg-orange-50"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => handlePlanningDecision(req, "Available")}
                                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${planningDecisions[req._id] === "Available"
                                  ? "bg-emerald-500 text-white shadow-sm"
                                  : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                  }`}
                              >
                                <FaCheck className="w-3 h-3 mr-1" />
                                Available
                              </button>
                              <button
                                onClick={() => handlePlanningDecision(req, "Procure")}
                                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${planningDecisions[req._id] === "Procure"
                                  ? "bg-orange-500 text-white shadow-sm"
                                  : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                                  }`}
                              >
                                <ShoppingCart className="w-3 h-3 mr-1" />
                                Procure
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Procurement Items */}
            {selectedPlanningProject && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
                <div className="border-b border-gray-200 px-6 py-4 bg-orange-50">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <ShoppingCart className="mr-3 text-orange-500 w-6 h-6" />
                    Items to Procure
                  </h2>
                </div>
                <div className="p-6">
                  {procurementItems.length > 0 ? (
                    <>
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full">
                          <thead className="bg-gray-50 border border-gray-200 rounded-lg">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider rounded-l-lg">Item</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Quantity</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit Cost</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Cost</th>
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {procurementItems.map(item => (
                              <tr key={item._id} className="hover:bg-gray-50 transition-colors duration-150">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {item.name}
                                  {item.type && (
                                    <div className="text-xs text-gray-500">type: {item.type.charAt(0).toUpperCase() + item.type.slice(1)}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{item.quantity} {item.unit}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">${item.unitCost}</td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">${item.totalCost}</td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => removeProcurementItem(item)}
                                    className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-600 rounded-full hover:bg-red-200 hover:text-red-800 transition-colors duration-200"
                                    title="Remove Item"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-sm font-medium text-orange-900">
                          {planningTotals.totalItems} items | Total: <span className="font-bold">${planningTotals.totalValue?.toLocaleString()}</span>
                        </div>
                        <button
                          onClick={() => openPlanningModal(null, "procurement")}
                          className="inline-flex items-center px-6 py-3 rounded-lg bg-orange-500 text-white font-medium hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                        >
                          Create Procurement Plan
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-500 text-center py-6">No items currently marked for procurement.</div>
                  )}
                </div>
              </div>
            )}

            {/* Modals */}
            {activeModal === "procurement" && selectedPlanningProjectData && (
              <CreateProcurementPlanModal
                isOpen={true}
                onClose={closePlanningModal}
                project={selectedPlanningProjectData}
                itemsToProcure={procurementItems}
              />
            )}

            {activeModal === "requirement" && activeRequirement && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div
                  className="absolute inset-0 bg-black bg-opacity-50"
                  onClick={() => closePlanningModal(false)}
                />
                <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
                  <div className="bg-orange-100 p-4 border-b flex items-center justify-between">
                    <h3 className="font-semibold text-lg">Requirement Details</h3>
                    <button onClick={() => closePlanningModal(false)} className="p-1 hover:bg-orange-200 rounded">
                      <FaTimes className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="text-xl font-bold text-left text-gray-800 border-b pb-2">
                      {activeRequirement.name}
                    </div>
                    <table className="w-full text-sm text-left border-collapse">
                      <tbody>
                        {[
                          ["Type", activeRequirement.type ? activeRequirement.type.charAt(0).toUpperCase() + activeRequirement.type.slice(1) : "-"],
                          ["Availability Status", activeRequirement.availabilityStatus || "Pending"],
                          ["Quantity", `${activeRequirement.quantity} ${activeRequirement.unit}`],
                          ["Unit Cost", `$${activeRequirement.unitCost}`],
                          ["Total Cost", `$${activeRequirement.totalCost}`],
                          ["Specification", activeRequirement.specification || "-"],
                          ["Status", activeRequirement.status],
                        ].map(([label, value]) => (
                          <tr key={label} className="border-b last:border-0">
                            <td className="py-2 pr-4 text-gray-500 text-xs w-1/3">{label}</td>
                            <td className="py-2 font-medium text-gray-800 whitespace-pre-wrap">{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="flex justify-end">
                      <button
                        onClick={() => closePlanningModal(false)}
                        className="px-4 py-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Travel Logistics Dashboard - Integrated */}
        {reportType === "travel_logistics" && (
          <div className="space-y-6">
            {/* Travel Summary Stats */}
            <DashboardSummary summaryData={travelSummary} />

            {/* Travel Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Filter Travel Requests</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={travelFilters.status}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, status: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>

                <input
                  type="text"
                  placeholder="Destination"
                  value={travelFilters.destination}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, destination: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />

                <select
                  value={travelFilters.dateRange}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, dateRange: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Dates</option>
                  <option value="upcoming">Upcoming (7 days)</option>
                  <option value="this_month">This Month</option>
                  <option value="past">Past Travels</option>
                </select>
              </div>
            </div>

            {/* Travel Requests List */}
            <div className="grid grid-cols-1 gap-4">
              {travelRequests.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600">No travel requests found.</p>
                </div>
              ) : (
                travelRequests.map((request) => (
                  <TravelRequestCard
                    key={request._id}
                    request={request}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onAddItinerary={handleAddItinerary}
                    onReviewReport={handleReviewReport}
                  />
                ))
              )}
            </div>

            {travelLoading && travelRequests.length > 0 && (
              <div className="text-center mt-4">
                <div className="animate-spin h-6 w-6 border-2 rounded-full border-orange-500 border-t-transparent mx-auto"></div>
              </div>
            )}
          </div>
        )}

        {/* KPI Cards - Not shown for travel logistics or project planning */}
        {reportType !== "travel_logistics" && reportType !== "project_planning" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {getKPICards().map((card, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
                    <p className={`text-3xl font-bold ${card.textColor}`}>{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                </div>
                <div className="mt-4 flex items-center text-xs text-gray-500">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Updated now
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Charts Section - Not shown for travel logistics, project planning, or contract report (since we have special tables) */}
        {reportType !== "travel_logistics" && reportType !== "project_planning" && reportType !== "contract" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 mb-8">
            {/* Status Distribution */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Status Distribution</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={currentData.byStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {currentData.byStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timeline Trend */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Trend Analysis (6 Months)</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={currentData.timeline}>
                    <defs>
                      <linearGradient id="colorFulfilled" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDelayed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="fulfilled"
                      stackId="1"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#colorFulfilled)"
                      name="Fulfilled"
                    />
                    <Area
                      type="monotone"
                      dataKey="pending"
                      stackId="1"
                      stroke="#f59e0b"
                      fillOpacity={1}
                      fill="url(#colorPending)"
                      name="Pending"
                    />
                    <Area
                      type="monotone"
                      dataKey="delayed"
                      stackId="1"
                      stroke="#ef4444"
                      fillOpacity={1}
                      fill="url(#colorDelayed)"
                      name="Delayed"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Contract Special Tables - Only for contract report */}
        {reportType === "contract" && (
          <div className="space-y-6">
            {/* Expired Contracts Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Expired Contracts</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const now = new Date()
                      const expiredContracts = currentData.details.filter(item =>
                        item.status === 'expired' ||
                        (item.endDate && new Date(item.endDate) < now)
                      )

                      return expiredContracts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                            No expired contracts found.
                          </td>
                        </tr>
                      ) : (
                        expiredContracts.map((contract) => (
                          <tr key={contract._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.vendor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.endDate ? new Date(contract.endDate).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.value || "N/A"}
                            </td>
                          </tr>
                        ))
                      )
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expiring Soon Contracts Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Contracts Expiring Soon (Within 30 Days)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const now = new Date()
                      const thirtyDaysFromNow = new Date()
                      thirtyDaysFromNow.setDate(now.getDate() + 30)

                      const expiringSoonContracts = currentData.details.filter(item => {
                        if (!item.endDate) return false
                        const endDate = new Date(item.endDate)
                        return endDate > now && endDate <= thirtyDaysFromNow
                      })

                      return expiringSoonContracts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                            No contracts expiring soon.
                          </td>
                        </tr>
                      ) : (
                        expiringSoonContracts.map((contract) => (
                          <tr key={contract._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.vendor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.endDate ? new Date(contract.endDate).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.value || "N/A"}
                            </td>
                          </tr>
                        ))
                      )
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Data Table - Not shown for travel logistics, project planning, or contract report (since we have special tables) */}
        {reportType !== "travel_logistics" && reportType !== "project_planning" && reportType !== "contract" && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 lg:mb-0">
                  Detailed{" "}
                  {reportType === "procurement"
                    ? "Procurement"
                    : reportType === "requisitions"
                      ? "Requisitions"
                      : reportType === "vendor"
                        ? "Vendor"
                        : reportType === "contract"
                          ? "Contract"
                          : "Project Planning"}{" "}
                  Records
                </h3>

                {/* Search and Filter Controls */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setCurrentPage(1)
                      }}
                      className="pl-9 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  >
                    <option value="all">All Statuses</option>
                    {reportType === "vendor" && (
                      <>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </>
                    )}
                    {reportType === "contract" && (
                      <>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="renewing">Renewing</option>
                      </>
                    )}
                    {reportType === "project_planning" && (
                      <>
                        <option value="planning">Planning</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                      </>
                    )}
                    {(reportType === "procurement" || reportType === "requisitions") && (
                      <>
                        <option value="pending">Pending</option>
                        <option value="fulfilled">Fulfilled</option>
                        <option value="delayed">Delayed</option>
                      </>
                    )}
                  </select>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 ${viewMode === "grid" ? "bg-orange-100 text-orange-800" : "bg-white text-gray-700"}`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 ${viewMode === "list" ? "bg-orange-100 text-orange-800" : "bg-white text-gray-700"}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {viewMode === "list" ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Name" :
                          reportType === "contract" ? "Title" :
                            reportType === "project_planning" ? "Project Title" : "Type"}
                      </th>
                      {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Email" :
                          reportType === "contract" ? "Vendor" :
                            reportType === "project_planning" ? "Status" : "Description"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Created Date" :
                          reportType === "contract" ? "Start Date" :
                            reportType === "project_planning" ? "Start Date" : "Request Date"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Contract Expiry" :
                          reportType === "contract" ? "End Date" :
                            reportType === "project_planning" ? "End Date" : "Expected Date"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" || reportType === "contract" || reportType === "project_planning" ? "Value" :
                          reportType === "procurement" ? "Value" : reportType === "requisitions" ? "Estimated Value" : "Cost"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-400">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item) => (
                        <tr key={item._id || item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item._id || item.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {reportType === "vendor" ? item.name :
                                reportType === "contract" ? item.title :
                                  reportType === "project_planning" ? item.projectTitle : item.type}
                            </span>
                          </td>
                          {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.project || "N/A"}</td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {reportType === "vendor" ? item.email :
                              reportType === "contract" ? item.vendor :
                                reportType === "project_planning" ? item.status : (item.description || item.title || "N/A")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(item.status)}
                              <span
                                className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item.status)}`}
                              >
                                {item.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.value || item.cost || "N/A"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6">
                {paginatedData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No records found.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedData.map((item) => (
                      <div key={item._id || item.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {reportType === "vendor" ? item.name :
                                reportType === "contract" ? item.title :
                                  reportType === "project_planning" ? item.projectTitle : item.type}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {item._id || item.id}
                            </p>
                          </div>
                          <div className="flex items-center">
                            {getStatusIcon(item.status)}
                            <span
                              className={`ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item.status)}`}
                            >
                              {item.status}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                            <div>
                              <span className="text-gray-500">Project:</span>
                              <span className="ml-2 text-gray-900">{item.project || "N/A"}</span>
                            </div>
                          )}

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Email:" :
                                reportType === "contract" ? "Vendor:" :
                                  reportType === "project_planning" ? "Status:" : "Description:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {reportType === "vendor" ? item.email :
                                reportType === "contract" ? item.vendor :
                                  reportType === "project_planning" ? item.status : (item.description || item.title || "N/A")}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Created:" :
                                reportType === "contract" ? "Start Date:" :
                                  reportType === "project_planning" ? "Start Date:" : "Requested:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "N/A"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Expiry:" :
                                reportType === "contract" ? "End Date:" :
                                  reportType === "project_planning" ? "End Date:" : "Expected:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "N/A"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">Value:</span>
                            <span className="ml-2 text-gray-900 font-medium">
                              {item.value || item.cost || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                          <button className="text-orange-600 hover:text-orange-800 text-sm font-medium flex items-center">
                            <MoreHorizontal className="w-4 h-4 mr-1" />
                            Actions
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredDetails.length)}</span>{" "}
                      of <span className="font-medium">{filteredDetails.length}</span> results
                      </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                          ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Travel Modals */}
    <RejectModal
      isOpen={isRejectModalOpen}
      onClose={() => {
        setIsRejectModalOpen(false)
        setSelectedRequestId(null)
      }}
      onSubmit={handleRejectSubmit}
    />

    <AddItineraryModal
      isOpen={isItineraryModalOpen}
      onClose={() => {
        setIsItineraryModalOpen(false)
        setSelectedRequestId(null)
      }}
      onSubmit={handleItinerarySubmit}
    />

      </div>
    </main>
  </div>
</div>
)
}
export default ProcurementLogisticsReports